import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as videoApi from '../apis/videos';

// 쿼리 키 상수화
export const VIDEO_KEYS = {
    all: ['videos'],
    list: (params) => [...VIDEO_KEYS.all, 'list', params],
};

/* ==========================================
    Queries (GET)
========================================== */

// 1. 동영상 목록 조회
export const useVideoList = (params) => {
    return useQuery({
        queryKey: VIDEO_KEYS.list(params),
        queryFn: () => videoApi.fetchVideoList(params),
        enabled: !!params.courseId, // 코스 ID가 있을 때만 조회
    });
};

/* ==========================================
    Mutations
========================================== */

// 1. 동영상 통합 업로드
/**
 * * 프로세스:
 * 1. Midibus 인증 토큰 발급
 * 2. Midibus 서버에 실제 동영상 파일 업로드
 * 3. 업로드된 동영상의 mediaId 획득
 * 4. 획득한 mediaId와 메타데이터를 DB에 저장
 */
export const useUploadMidibusMedia = () => {
    return useMutation({
        mutationFn: async ({ file, videoName, courseId }) => {
            // 1. 미디버스 토큰 발급
            const tokenRes = await videoApi.fetchMidibusToken();
            const token = tokenRes.data?.data?.token;

            if (!token) {
                throw new Error('미디버스 토큰 발급 실패');
            }

            // 2. 미디버스 카테고리 목록 조회
            const categoriesRes = await videoApi.fetchMidibusCategories(token);
            const categories = categoriesRes.data?.category_list || [];
            
            // 3. courseId와 일치하는 category_name을 가진 카테고리 찾기
            const matchedCategory = categories.find(
                category => {
                    const categoryName = category.category_name || category.name;
                    return categoryName === String(courseId);
                }
            );

            if (!matchedCategory) {
                throw new Error(`미디버스 카테고리를 찾을 수 없습니다. (courseId: ${courseId})`);
            }

            const midibusCategoryId = matchedCategory.category_id || matchedCategory.id;

            if (!midibusCategoryId) {
                throw new Error('미디버스 카테고리 ID를 찾을 수 없습니다.');
            }

            // 4. 미디버스에 동영상 업로드
            const formData = new FormData();
            formData.append('file', file);
            formData.append('size', file.size);
            formData.append('name', videoName || file.name);

            const uploadRes = await videoApi.uploadMidibusMedia({
                token,
                categoryId: midibusCategoryId,
                formData,
            });

            // 5. 업로드 결과에서 mediaId 추출
            if (uploadRes.data.success) {
                return uploadRes.data.media_id;
            } else {
                throw new Error('미디버스 업로드에 실패했습니다. mediaId를 받을 수 없습니다.');
            }
        },
    });
};

export const useUploadAndCreateVideo = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ file, videoData, midibusCategoryId }) => {
            // 1. 미디버스 토큰 발급
            const tokenRes = await videoApi.fetchMidibusToken();
            const token = tokenRes.data?.data?.token;
            
            if (!token) {
                throw new Error('미디버스 토큰 발급 실패');
            }

            // 2. 미디버스 업로드용 FormData 생성
            const formData = new FormData();
            formData.append('file', file);
            formData.append('size', file.size);
            formData.append('name', videoData.videoName);
            // 기타 미디버스 필수 파라미터가 있다면 추가 (예: title, description 등)

            // 3. 미디버스에 업로드
            const uploadRes = await videoApi.uploadMidibusMedia({
                token,
                categoryId: midibusCategoryId, // 미디버스 상의 카테고리 ID (사전에 조회하거나 생성된 ID)
                formData,
            });

            // 4. 업로드 결과에서 mediaId 추출
            // 미디버스 API 응답 구조에 따라 경로 수정 필요
            const mediaId = uploadRes.data?.data?.media_id || uploadRes.data?.media_id || uploadRes.data?.data?.id || uploadRes.data?.id;

            if (!mediaId) {
                throw new Error('Midibus Upload Failed: Media ID not returned.');
            }

            // 5. DB에 메타데이터 저장
            const dbRequestData = {
                ...videoData, // courseId, courseType, videoSeq, videoName, videoIntro, videoObject
                mediaId: mediaId, // 미디버스에서 받은 ID 매핑
            };

            return await videoApi.createVideo(dbRequestData);
        },
        onSuccess: () => {
            // 목록 갱신
            queryClient.invalidateQueries({ queryKey: VIDEO_KEYS.all });
        },
    });
};

// 2. 동영상 수정
export const useUpdateVideo = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: videoApi.updateVideo,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: VIDEO_KEYS.all });
        },
    });
};

// 3. 미디버스 동영상 삭제 (DB 삭제 없이 미디버스만 삭제)
export const useDeleteMidibusMedia = () => {
    return useMutation({
        mutationFn: async ({ courseId, mediaId }) => {
            // 1. 미디버스 토큰 발급
            const tokenRes = await videoApi.fetchMidibusToken();
            const token = tokenRes.data?.data?.token;

            if (!token) {
                throw new Error('미디버스 토큰 발급 실패');
            }

            // 2. 미디버스 카테고리 목록 조회
            const categoriesRes = await videoApi.fetchMidibusCategories(token);
            const categories = categoriesRes.data?.category_list || [];
            
            // 3. courseId와 일치하는 category_name을 가진 카테고리 찾기
            const matchedCategory = categories.find(
                category => {
                    const categoryName = category.category_name || category.name;
                    return categoryName === String(courseId);
                }
            );

            if (!matchedCategory) {
                throw new Error(`미디버스 카테고리를 찾을 수 없습니다. (courseId: ${courseId})`);
            }

            const midibusCategoryId = matchedCategory.category_id || matchedCategory.id;

            if (!midibusCategoryId) {
                throw new Error('미디버스 카테고리 ID를 찾을 수 없습니다.');
            }

            // 4. 미디버스에서 동영상 삭제
            try {
                await videoApi.deleteMidibusMedia({
                    token,
                    categoryId: midibusCategoryId,
                    mediaId: mediaId,
                });
            } catch (error) {
                console.warn(`[Midibus Delete Fail] MediaID: ${mediaId}`, error);
                throw error;
            }
        },
    });
};

// 4. 동영상 삭제 (DB + 미디버스)
export const useDeleteVideo = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ videoId, midibusCategoryId, midibusMediaId }) => {
            // 1. 토큰 발급
            const tokenRes = await videoApi.fetchMidibusToken();
            const token = tokenRes.data?.data?.token;

            if (!token) throw new Error('미디버스 토큰 발급 실패');

            // 2. 미디버스에서 영상 삭제 시도
            try {
                await videoApi.deleteMidibusMedia({
                    token,
                    categoryId: midibusCategoryId,
                    mediaId: midibusMediaId
                });
            } catch (error) {
                console.warn(`[Midibus Delete Fail] MediaID: ${midibusMediaId}`, error);
            }

            // 3. DB에서 메타데이터 삭제 (최종)
            return await videoApi.deleteVideo(videoId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: VIDEO_KEYS.all });
        },
    });
};